import { v4 as uuidv4 } from 'uuid';

import { analyticsEvents } from '../api/analytics';
import { Sentry } from './sentry';

// Contract: POST /v3/analytics/events (formue-api
// docs/analytics-client-integration.md). One request per event, no queue and no
// retry — the server resolves identity and impersonation at send time, so
// sending immediately is what keeps attribution correct. A failed send is lost.
// The web and mobile adapters call in here; everything platform specific
// (route patterns, navigation state, device context) stays on their side.

const SESSION_KEY = 'analytics:session';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

let started = false;
let client = null;
let getContext = null;
// Web Storage shape (getItem/setItem/removeItem, sync) or null on mobile.
let storage = null;
let session = null;
// Referrer/page is the previous tracked view within this start only.
let lastViewName = null;
let fourHundredReported = false;

const warn = (error) => {
  if (process.env.NODE_ENV === 'development') {
    console.warn('Analytics event not sent', error);
  }
};

// A 400 means the client is sending a shape the API rejects — worth one Sentry
// report per start. Everything else (401, 403, 5xx, network) is expected noise.
const handleFailure = async (error, eventType, name) => {
  try {
    warn(error);
    if (error?.status !== 400 || fourHundredReported) return;
    fourHundredReported = true;

    let details;
    try {
      // apiFetch throws before reading the body, so the response is unconsumed.
      const body = await error.response.json();
      details = body?.error?.details;
    } catch {
      // Body unavailable: report without details rather than not at all.
    }

    Sentry.captureMessage('Analytics event rejected by the API', {
      level: 'warning',
      extra: { client, eventType, name, details },
    });
  } catch {
    // Reporting a failure must never fail.
  }
};

const getSessionId = () => {
  const now = Date.now();
  if (storage) {
    try {
      session = JSON.parse(storage.getItem(SESSION_KEY));
    } catch {
      // Missing or unparsable: start a new session below.
      session = null;
    }
  }
  if (!session?.id || now - session.lastActivityAt >= SESSION_TIMEOUT_MS) {
    session = { id: uuidv4() };
  }
  session.lastActivityAt = now;
  if (storage) {
    storage.setItem(SESSION_KEY, JSON.stringify(session));
  }
  return session.id;
};

// Unknown keys are a 400, so the event carries exactly the contract keys.
const send = (eventType, name, { resourceId, properties } = {}) => {
  const event = {
    clientEventId: uuidv4(),
    eventType,
    client,
    name,
    sessionId: getSessionId(),
    occurredAt: new Date().toISOString(),
  };
  try {
    event.context = getContext();
  } catch {
    // Context is best effort; send the event without it.
  }
  if (resourceId != null && resourceId !== '') {
    event.resourceId = String(resourceId);
  }
  if (properties && Object.keys(properties).length) {
    event.properties = properties;
  }

  analyticsEvents
    .post([event])
    .catch((error) => handleFailure(error, eventType, name));
};

/**
 * Track a screen/page view. `properties.referrer` is the previous view's name,
 * or null for the first view after start. No-op before start / after stop.
 */
export const trackView = (name, { resourceId, properties } = {}) => {
  try {
    if (!started) return;
    const referrer = lastViewName;
    lastViewName = name;
    send(client === 'app' ? 'screen_view' : 'page_view', name, {
      resourceId,
      properties: { ...properties, referrer },
    });
  } catch (error) {
    warn(error);
  }
};

/**
 * Track a custom event. `properties.page` is the last tracked view's name, or
 * null if there was none. No-op before start / after stop.
 */
export const trackEvent = (name, { resourceId, properties } = {}) => {
  try {
    if (!started) return;
    send('track', name, {
      resourceId,
      properties: { ...properties, page: lastViewName },
    });
  } catch (error) {
    warn(error);
  }
};

/**
 * Start tracking. `client` is 'web' or 'app', `getContext` is called per event
 * (viewport and the like change), `storage` is optional and only makes the
 * session durable across reloads. Idempotent: the first call's options win.
 */
export const startAnalytics = (options = {}) => {
  try {
    if (started) return;
    started = true;
    client = options.client;
    getContext = options.getContext;
    storage = options.storage ?? null;
    fourHundredReported = false;
  } catch (error) {
    warn(error);
  }
};

/**
 * Logout (incl. idle timeout): stop tracking and drop the session, so nothing
 * is sent until the next startAnalytics.
 */
export const stopAnalytics = () => {
  try {
    started = false;
    session = null;
    lastViewName = null;
    fourHundredReported = false;
    if (storage) {
      storage.removeItem(SESSION_KEY);
    }
  } catch (error) {
    warn(error);
  }
};
